import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useOrganization } from '../../hooks/useOrganization';
import { Package, Loader2, CheckCircle2, AlertCircle, RefreshCw, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Suivi persistant des imports par lot.
 * Le toast de lancement est éphémère : ce widget poll cv_batch_jobs (RLS org)
 * tant qu'un lot est actif, et donne le détail par CV (réussis / en échec).
 */

interface BatchJob {
    id: number;
    status: 'submitted' | 'processing' | 'harvesting' | 'done' | 'failed';
    total_cvs: number;
    processed_cvs: number;
    failed_cvs: number;
    candidate_ids: number[];
    created_at: string;
    updated_at: string;
}

interface BatchCandidate {
    id: number;
    filename: string | null;
    full_name: string | null;
    status: string;
    error_details: string | null;
}

const ACTIVE_STATUSES = ['submitted', 'processing', 'harvesting'];
const POLL_MS = 15_000;
// On n'affiche les lots terminés que s'ils ont fini récemment (sinon bruit permanent)
const DONE_VISIBLE_MS = 30 * 60 * 1000;

// Résumé lisible d'une erreur technique
const shortError = (error?: string | null): string => {
    if (!error) return 'Erreur';
    if (error.includes('429') || error.includes('RESOURCE_EXHAUSTED')) return 'Service saturé — re-déposer le fichier';
    if (error.includes('Unterminated') || error.includes('Unexpected end') || error.includes('JSON')) return "Analyse incomplète — re-déposer le fichier";
    if (error.includes('23505') || error.includes('already exists')) return 'Doublon (email déjà présent)';
    return error.length > 55 ? error.slice(0, 52) + '…' : error;
};

const displayName = (c: BatchCandidate): string =>
    c.full_name && c.full_name.trim() ? c.full_name : (c.filename ?? `Candidat #${c.id}`).replace(/^CV\//, '');

interface BatchImportWidgetProps {
    /** Incrémenté par le parent au lancement d'un lot. Le sondage tourne toutes les
     *  15 s : sans ce signal, rien ne changeait à l'écran jusqu'au tour suivant, et on
     *  croyait que l'import ne partait pas (constaté au premier import réel, 2026-08-21). */
    refreshSignal?: number;
    /** Nombre de CV en cours d'envoi vers create-cv-batch. La ligne cv_batch_jobs n'est
     *  ecrite qu'a la TOUTE FIN de cette fonction — apres lecture des fichiers, encodage
     *  base64 et upload chez Google. Sur 10 CV cela a pris 30 s pendant lesquelles il n'y
     *  avait rien a afficher, et on croyait que rien ne partait. Cette carte locale couvre
     *  exactement cet intervalle, puis cede la place au vrai suivi.
     */
    sendingCount?: number;
    /** Appelé dès que des CV du lot viennent d'aboutir. La liste des talents ne se
     *  rafraîchissait pas seule : un lot n'alimente pas activeUploads, donc rien ne la
     *  prévenait et il fallait recharger la page à la main. */
    onCandidatesUpdated?: () => void;
}

const BatchImportWidget: React.FC<BatchImportWidgetProps> = ({ refreshSignal, onCandidatesUpdated, sendingCount = 0 }) => {
    const { org } = useOrganization();
    // Les ids déjà vus comme terminés : on ne prévient le parent que sur du nouveau,
    // sinon chaque tour de sondage relancerait une lecture de toute la liste.
    const seenCompletedRef = useRef<Set<number>>(new Set());
    // La callback passe par une ref : la mettre dans les dépendances de l'effet
    // relancerait le sondage à chaque rendu du parent.
    const onUpdatedRef = useRef(onCandidatesUpdated);
    useEffect(() => { onUpdatedRef.current = onCandidatesUpdated; }, [onCandidatesUpdated]);
    const [jobs, setJobs] = useState<BatchJob[]>([]);
    const [details, setDetails] = useState<BatchCandidate[]>([]);
    // Nombre de CV envoyes pour les lots visibles, c-a-d de fichiers deposes. Sert de
    // denominateur stable : il ne doit pas retrecir quand un lot se termine.
    const [expected, setExpected] = useState(0);
    const [showDetails, setShowDetails] = useState(false);
    const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        if (!org?.id) return;
        let stopped = false;
        let timer: ReturnType<typeof setInterval> | null = null;

        const fetchJobs = async () => {
            const { data } = await (supabase as any)
                .from('cv_batch_jobs')
                .select('id, status, total_cvs, processed_cvs, failed_cvs, candidate_ids, created_at, updated_at')
                .eq('organization_id', org.id)
                .gte('created_at', new Date(Date.now() - 24 * 3600 * 1000).toISOString())
                .order('id', { ascending: false });
            if (stopped) return;
            const jobList = (data as BatchJob[]) ?? [];
            setJobs(jobList);

            // Détail par CV des lots visibles (actifs + terminés récents)
            const visible = jobList.filter(j =>
                ACTIVE_STATUSES.includes(j.status)
                || Date.now() - new Date(j.updated_at).getTime() < DONE_VISIBLE_MS
            );
            const ids = [...new Set(visible.flatMap(j => j.candidate_ids ?? []))];
            if (ids.length > 0) {
                const { data: cands } = await (supabase as any)
                    .from('candidates')
                    .select('id, filename, full_name, status, error_details')
                    .in('id', ids);
                if (stopped) return;
                const list = (cands as BatchCandidate[]) ?? [];
                setDetails(list);
                setExpected(ids.length);

                const fresh = list
                    .filter(c => c.status === 'completed' && !seenCompletedRef.current.has(c.id))
                    .map(c => c.id);
                if (fresh.length > 0) {
                    fresh.forEach(id => seenCompletedRef.current.add(id));
                    onUpdatedRef.current?.();
                }
            } else if (!stopped) {
                setDetails([]);
                setExpected(0);
            }
        };

        fetchJobs();
        timer = setInterval(fetchJobs, POLL_MS);
        return () => { stopped = true; if (timer) clearInterval(timer); };
    }, [org?.id, refreshSignal]);

    const active = jobs.filter(j => ACTIVE_STATUSES.includes(j.status));
    const recentDone = jobs.filter(j =>
        (j.status === 'done' || j.status === 'failed')
        && Date.now() - new Date(j.updated_at).getTime() < DONE_VISIBLE_MS
        && !dismissedIds.has(j.id)
    );

    // Envoi en cours et aucun lot encore enregistre : on montre la carte locale.
    if (sendingCount > 0 && active.length === 0) {
        return (
            <div className="mb-2 rounded-xl border border-indigo-200 bg-indigo-50/50 animate-in fade-in">
                <div className="flex items-center gap-3 px-4 py-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                        <Package size={17} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Loader2 size={13} className="animate-spin text-indigo-500" />
                            Envoi de {sendingCount} CV en cours…
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Traitement par lot : les profils s'afficheront au fur et à mesure.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (active.length === 0 && recentDone.length === 0) return null;

    // Avancement reel. Deux pieges que l ancien calcul ignorait :
    //  - il sommait processed_cvs des lots ACTIFS, or ce compteur passe de 0 a 10 au
    //    moment meme ou le lot devient done et sort du decompte : le numerateur ne
    //    pouvait jamais bouger, et le denominateur retrecissait (0/64 puis 0/34).
    //  - la recolte SUPPRIME la fiche temporaire quand un CV fait doublon d email.
    //    Ces fiches n'existent plus, mais elles sont bien traitees : sans les compter,
    //    la barre resterait bloquee a 62/66.
    // Une fiche fusionnee porte le statut `duplicate` : elle est traitee, meme si elle
    // ne deviendra jamais un profil. Le `expected - details.length` reste en filet pour
    // les lots recoltes AVANT ce correctif, ou la fiche etait purement supprimee.
    const settledCount = details.filter(
        c => c.status === 'completed' || c.status === 'error' || c.status === 'duplicate',
    ).length + Math.max(0, expected - details.length);

    const okCands  = details.filter(c => c.status === 'completed');
    const koCands  = details.filter(c => c.status === 'error');
    const mergedCands = details.filter(c => c.status === 'duplicate');
    // `duplicate` doit sortir des « en cours » : ces fiches sont traitees, elles ne
    // deviendront simplement jamais un profil. Sans cette exclusion elles gardaient un
    // spinner et « En cours » indefiniment, alors que l'import etait termine.
    const pending  = details.filter(c => c.status !== 'completed' && c.status !== 'error' && c.status !== 'duplicate');

    const detailList = showDetails && details.length > 0 && (
        <div className="border-t border-slate-100 max-h-56 overflow-y-auto divide-y divide-slate-50">
            {[...koCands, ...pending, ...mergedCands, ...okCands].map(c => (
                <div key={c.id} className="flex items-center gap-2.5 px-4 py-1.5">
                    <span className="flex-shrink-0">
                        {c.status === 'error' ? <AlertCircle size={14} className="text-red-500" /> :
                         c.status === 'completed' ? <CheckCircle2 size={14} className="text-emerald-500" /> :
                         c.status === 'duplicate' ? <CheckCircle2 size={14} className="text-slate-400" /> :
                         <Loader2 size={14} className="text-indigo-500 animate-spin" />}
                    </span>
                    <span className="truncate text-slate-700 flex-1 min-w-0 text-xs" title={c.filename ?? undefined}>{displayName(c)}</span>
                    <span className={`text-[11px] flex-shrink-0 ${c.status === 'error' ? 'text-red-500' : c.status === 'completed' ? 'text-emerald-600' : 'text-slate-400'}`}
                          title={c.status === 'error' ? (c.error_details ?? undefined) : undefined}>
                        {c.status === 'error' ? shortError(c.error_details)
                            : c.status === 'completed' ? 'Analysé'
                            : c.status === 'duplicate' ? 'Même personne'
                            : 'En cours'}
                    </span>
                </div>
            ))}
        </div>
    );

    const detailToggle = details.length > 0 && (
        <button onClick={() => setShowDetails(s => !s)}
            className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 text-slate-500 hover:bg-slate-100 rounded-md transition-colors flex-shrink-0">
            {showDetails ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Détail
        </button>
    );

    // ── Lots actifs : bandeau « ça tourne » ──────────────────────────────────
    if (active.length > 0) {
        // Sur l ensemble des lots visibles, pas seulement les actifs : le total doit
        // rester celui de l import entier tant qu il n est pas fini.
        const total = expected || active.reduce((s, j) => s + j.total_cvs, 0);
        const processed = settledCount;
        const harvesting = active.some(j => j.status === 'harvesting');
        return (
            <div className="mb-2 rounded-xl border border-indigo-200 bg-indigo-50/50 animate-in fade-in">
                <div className="flex items-center gap-3 px-4 py-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                        <Package size={17} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Loader2 size={13} className="animate-spin text-indigo-500" />
                            Import par lot en cours — {processed}/{total} CV traités
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {harvesting
                                ? 'Finalisation des analyses — les profils arrivent…'
                                : "L'analyse tourne en arrière-plan. Les profils apparaîtront automatiquement d'ici quelques minutes."}
                        </p>
                    </div>
                    <div className="h-1.5 w-32 bg-indigo-100 rounded-full overflow-hidden flex-shrink-0 hidden sm:block">
                        <div className="h-full bg-indigo-500 rounded-full transition-all duration-700"
                             style={{ width: `${total > 0 ? Math.max(4, Math.round((processed / total) * 100)) : 4}%` }} />
                    </div>
                    {detailToggle}
                </div>
                {detailList}
            </div>
        );
    }

    // Regroupement des fusions par profil d arrivee. Une fiche `duplicate` porte l id
    // de sa cible dans `error_details` — meme convention que la voie synchrone. Les
    // deux CV viennent du meme import, donc la cible est toujours dans `details`.
    const mergeGroups = (() => {
        const groups = new Map<number, { targetId: number; files: Array<{ id: number; name: string; retained: boolean }> }>();
        for (const t of details.filter(c => c.status === 'duplicate' && c.error_details)) {
            const targetId = parseInt(String(t.error_details), 10);
            if (!Number.isFinite(targetId)) continue;
            if (!groups.has(targetId)) {
                const target = details.find(c => c.id === targetId);
                groups.set(targetId, {
                    targetId,
                    files: target
                        ? [{ id: target.id, name: target.filename ?? displayName(target), retained: true }]
                        : [],
                });
            }
            groups.get(targetId)!.files.push({ id: t.id, name: t.filename ?? `Candidat #${t.id}`, retained: false });
        }
        return [...groups.values()].filter(g => g.files.length > 1);
    })();

    // ── Lots terminés récemment : bilan + détail ─────────────────────────────
    const doneOk = recentDone.reduce((s, j) => s + (j.processed_cvs - j.failed_cvs), 0);
    const doneKo = recentDone.reduce((s, j) => s + j.failed_cvs, 0);
    return (
        <div className={`mb-2 rounded-xl border animate-in fade-in ${doneKo > 0 ? 'border-amber-200 bg-amber-50/40' : 'border-emerald-200 bg-emerald-50/40'}`}>
            <div className="flex items-center gap-3 px-4 py-3">
                {doneKo > 0
                    ? <AlertCircle size={18} className="text-amber-500 flex-shrink-0" />
                    : <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />}
                <p className="text-sm text-slate-700 flex-1 min-w-0">
                    Import par lot terminé — <span className="font-semibold text-emerald-700">{doneOk} CV analysé{doneOk > 1 ? 's' : ''}</span>
                    {doneKo > 0 && <span className="font-semibold text-red-600">, {doneKo} en échec</span>}
                    {doneKo > 0 && <span className="text-slate-500"> (voir le détail, puis re-déposez les fichiers concernés)</span>}
                </p>
                {detailToggle}
                <button onClick={() => window.location.reload()}
                    className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded-md transition-colors flex-shrink-0">
                    <RefreshCw size={12} /> Actualiser
                </button>
                <button onClick={() => setDismissedIds(prev => new Set([...prev, ...recentDone.map(j => j.id)]))} title="Fermer"
                    className="text-slate-300 hover:text-slate-500 p-1 rounded transition-colors flex-shrink-0">
                    <X size={15} />
                </button>
            </div>
            {mergeGroups.length > 0 && (
                <div className="border-t border-slate-100 px-4 py-3 space-y-3">
                    {mergeGroups.map(g => (
                        <div key={g.targetId}>
                            <p className="text-xs font-semibold text-slate-700">
                                {g.files.length} profils concernant la même personne
                            </p>
                            <ul className="mt-1 space-y-0.5">
                                {g.files.map(f => (
                                    <li key={f.id} className="text-xs text-slate-600 flex items-baseline gap-1.5">
                                        <span className="text-slate-300 flex-shrink-0">•</span>
                                        <span className="truncate" title={f.name}>{f.name}</span>
                                        {f.retained && (
                                            <span className="text-emerald-700 font-medium flex-shrink-0">
                                                (Profil retenu pour l'import)
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
            {detailList}
        </div>
    );
};

export default BatchImportWidget;
